/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-02-11 14:28:53
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import moment from 'moment';
import React, { Component } from 'react';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { TextStyle } from '@shopify/polaris';
// import { URL } from 'src/constants/links';

// import { RenderTooltip } from 'src/components/views/common/render-tooltip';
import { nowTimestamp, formatNumberInThousand } from 'src/helpers';
import { DATE_FORMAT } from 'src/constants/language-setting';
import { formatPriceDisplay, getDiscountCodeValue } from 'src/helpers/subscription-v3-helper';

class PlanNoticeStatus extends Component<WithI18nProps> {
  render() {
    let {
      i18n,
      tenantApp,
      subscription = {},
      packageInfo = {},
      discountCodeApplied,
      suggestDiscountCode = ''
    } = this.props;
    const {
      status = 'trial',
      trialExpiresAt = '',
      currentTotalProducts,
      regularExpiredAt
    } = subscription;
    const { title = '' } = packageInfo;
    const selectedPlan = _.get(tenantApp, 'selectedPlan', '');

    const trialExpiredAt = !_.isEmpty(trialExpiresAt)
      ? trialExpiresAt
      : _.get(tenantApp, 'trialExpiresAt', 0)
      ? moment(new Date(_.get(tenantApp, 'trialExpiresAt', 0) * 1000)).format('LL')
      : '';

    const [todayDateString] = new Date().toISOString().split('T');
    const today = new Date(todayDateString).getTime() / 1000;

    // Applied discount message
    const appliedDiscountMessage = this.renderNoticeAppliedDiscount();

    const discountMessage =
      suggestDiscountCode === 'BFCM22' ? (
        <span>
          {i18n.translate('AdminHTML.SubscriptionV3.PlanStatus.suggestDiscountCode', {
            t1: (
              <span style={{ color: '#de3618' }}>
                {i18n.translate('AdminHTML.SubscriptionV3.PlanStatus.suggestDiscountCode_t1')}
              </span>
            )
          })}
        </span>
      ) : (
        ''
      );

    const statusNotice = {
      trial: (
        <span>
          {i18n.translate('AdminHTML.SubscriptionV3.PlanStatus.statusNotice_Trial', {
            date: <span style={{ color: '#de3618' }}>{trialExpiredAt}</span>
          })}
        </span>
      ),
      expired: (
        <span>
          {i18n.translate('AdminHTML.SubscriptionV3.PlanStatus.statusNotice_Expired', {
            date: <span style={{ color: '#de3618' }}>{trialExpiredAt}</span>
          })}
        </span>
      ),
      regularExpired: (
        <span>
          {i18n.translate(
            'AdminHTML.SubscriptionV3.PlanStatus.statusNotice_regularPlanExpired_P1',
            {
              date: <TextStyle variation="strong">{regularExpiredAt}</TextStyle>,
              t1: (
                <span>
                  {i18n.translate(
                    `AdminHTML.SubscriptionV3.PlanStatus.statusNotice_regularPlanExpired_P2`
                  )}
                </span>
              )
            }
          )}
        </span>
      ),
      expiredSoon: (
        <span>
          {i18n.translate('AdminHTML.SubscriptionV3.PlanStatus.statusNotice_ExpiredSoon', {
            t1: (
              <span style={{ color: '#de3618' }}>
                {i18n.translate('AdminHTML.SubscriptionV3.PlanStatus.statusNotice_ExpiredSoon_t1')}
              </span>
            ),
            nt_label: title ? i18n.translate(title) : title
          })}
        </span>
      ),
      regularExpiredSoon: (
        <span>
          {i18n.translate(
            'AdminHTML.SubscriptionV3.PlanStatus.statusNotice_regularPlanExpiredSoon_P1',
            {
              date: <TextStyle variation="strong">{regularExpiredAt}</TextStyle>,
              t1: (
                <span>
                  {i18n.translate(
                    `AdminHTML.SubscriptionV3.PlanStatus.statusNotice_regularPlanExpiredSoon_P2`
                  )}
                </span>
              )
            }
          )}
        </span>
      ),
      subscribed:
        selectedPlan === 'offer0_1' ? (
          <span>
            {i18n.translate(
              'AdminHTML.Dashboard.SubscriptionStatus.subscribed_PasswordProtectedOffer_Paragraph'
            )}
          </span>
        ) : selectedPlan.includes('plan') ? (
          discountMessage // Old plan
        ) : (
          <div>
            {!_.isEmpty(discountCodeApplied) &&
            _.get(discountCodeApplied, 'expiredAt', 0) > today ? (
              appliedDiscountMessage
            ) : (
              <p>{i18n.translate('AdminHTML.SubscriptionV3.PlanStatus.statusNotice_Subscribed')}</p>
            )}
          </div>
        ),
      exceededWithNotification: (
        <span style={{ color: '#de3618' }}>
          {i18n.translate(
            'AdminHTML.SubscriptionV3.PlanStatus.statusNotice_ExceededWithNotification'
          )}
        </span>
      ),
      exceeded: (
        <span style={{ color: '#de3618' }}>
          {i18n.translate('AdminHTML.SubscriptionV3.PlanStatus.statusNotice_Exceeded', {
            number: formatNumberInThousand(currentTotalProducts)
          })}
        </span>
      ),
      specialDiscount2021ExpiredSoon: this.renderNoticeSpecialDiscountExpiredSoon('bfcm21'),
      specialDiscountExpiredSoon: this.renderNoticeSpecialDiscountExpiredSoon(),
      oldSpecialDiscountExpired: this.renderNoticeSpecialDiscountExpired()
    };

    if (status === 'specialDiscountExpired') return statusNotice['subscribed'];
    return statusNotice[status];
  }
  renderNoticeSpecialDiscountExpiredSoon = status => {
    const { i18n, tenantApp = {} } = this.props;
    const specialDiscountExpiredAt = _.get(tenantApp, 'specialDiscountExpiredAt', nowTimestamp());
    const appLanguage = _.get(tenantApp, 'appLanguage', 'en');

    // Convert time to string
    const formatDateNumber = DATE_FORMAT[appLanguage].longDate;
    const expiredDate = moment(specialDiscountExpiredAt * 1000).format(formatDateNumber);

    return (
      <React.Fragment>
        <span style={{ color: '#916A00' }}>
          {i18n.translate(
            'AdminHTML.SubscriptionV3.PlanStatus.statusNotice_DiscountExpiredSoon_P1',
            {
              t1: <TextStyle variation="strong">{expiredDate}</TextStyle>
            }
          )}
        </span>{' '}
        {status === 'bfcm21' ? (
          <TextStyle>
            {i18n.translate(
              'AdminHTML.SubscriptionV3.PlanStatus.statusNotice_passwordProtectedOffer_ExpiredSoon_P2',
              {
                t1: (
                  <TextStyle variation="strong">
                    {i18n.translate(`AdminHTML.Common.Subscription.reSubscribe`)}
                  </TextStyle>
                )
              }
            )}
          </TextStyle>
        ) : (
          <TextStyle>
            {i18n.translate(
              'AdminHTML.SubscriptionV3.PlanStatus.statusNotice_DiscountExpiredSoon_P2',
              {
                t1: (
                  <TextStyle variation="strong">
                    {i18n.translate(`AdminHTML.Common.Subscription.reSubscribe`)}
                  </TextStyle>
                )
              }
            )}
          </TextStyle>
        )}
      </React.Fragment>
    );
  };

  renderNoticeSpecialDiscountExpired = () => {
    const { i18n, tenantApp = {} } = this.props;

    const specialDiscountExpiredAt = _.get(tenantApp, 'specialDiscountExpiredAt', nowTimestamp());
    const appLanguage = _.get(tenantApp, 'appLanguage', 'en');

    // Convert time to string
    const formatDateNumber = DATE_FORMAT[appLanguage].longDate;
    const expiredDate = moment(specialDiscountExpiredAt * 1000).format(formatDateNumber);

    return (
      <React.Fragment>
        <TextStyle variation="negative">
          {i18n.translate('AdminHTML.SubscriptionV3.PlanStatus.statusNotice_DiscountExpired_P1', {
            t1: <TextStyle variation="strong">{expiredDate}</TextStyle>
          })}
        </TextStyle>{' '}
        <TextStyle>
          {i18n.translate('AdminHTML.SubscriptionV3.PlanStatus.statusNotice_DiscountExpired_P2', {
            t1: (
              <TextStyle variation="strong">
                {i18n.translate(`AdminHTML.Common.Subscription.reSubscribe`)}
              </TextStyle>
            )
          })}
        </TextStyle>
      </React.Fragment>
    );
  };

  renderNoticeAppliedDiscount = () => {
    const { discountCodeApplied, i18n, currentInvoiceData } = this.props;

    const discountInfo = _.get(currentInvoiceData, 'discountInfo', {});
    const totalPrice = _.get(currentInvoiceData, 'totalPrice', 0);
    const originalPrice = _.get(currentInvoiceData, 'originalPrice', 0);
    const [todayDateString] = new Date().toISOString().split('T');
    const today = new Date(todayDateString).getTime() / 1000;

    // Expired date of discount duration
    const expiredAt = _.get(discountInfo, 'expiredAt', '');
    const duration = _.get(discountCodeApplied, 'duration', 0);

    let appliedDiscountMessage = '';

    if (!_.isEmpty(discountCodeApplied) && _.get(discountCodeApplied, 'expiredAt', 0) > today) {
      const startAt = _.get(discountCodeApplied, 'startAt', 0);
      const startFromDate = moment(startAt * 1000).format('MMM DD, YYYY');
      const { dcPostfixValue } = getDiscountCodeValue(discountCodeApplied);

      appliedDiscountMessage = (
        <p style={{ marginTop: '3px' }}>
          {!duration &&
            i18n.translate('AdminHTML.SubscriptionV3.PlanStatus.statusNotice_AppliedDiscount', {
              t1: (
                <TextStyle variation="strong">
                  {i18n.translate('AdminHTML.SubscriptionV3.PlanStatus.discountPercentBadge', {
                    nt_value: dcPostfixValue
                  })}
                </TextStyle>
              ),
              t2: (
                <TextStyle variation="strong">
                  {i18n.translate('AdminHTML.SubscriptionV3.PlanStatus.discountLifetime')}
                </TextStyle>
              ),
              t3: <TextStyle variation="strong">{startFromDate}</TextStyle>
            })}
          {duration &&
            duration > 0 &&
            i18n.translate('AdminHTML.SubscriptionV3.PlanStatus.discountInfo_Detail', {
              totalPrice: (
                <TextStyle variation="strong">${formatPriceDisplay(totalPrice)}</TextStyle>
              ),
              duration: (
                <TextStyle variation="strong">
                  {i18n.translate(
                    `AdminHTML.SubscriptionV3.PlanStatus.${
                      duration > 1 ? 'discountDurationPlural' : 'discountDuration'
                    }`,
                    {
                      nt_duration: duration
                    }
                  )}
                </TextStyle>
              ),
              expiredAt: <TextStyle variation="strong">{expiredAt}</TextStyle>,
              originalPrice: (
                <TextStyle variation="strong">
                  {i18n.translate('AdminHTML.SubscriptionV3.Common.pricePerMonth', {
                    price: formatPriceDisplay(originalPrice)
                  })}
                </TextStyle>
              )
            })}
          {/* <RenderTooltip
            content={i18n.translate(
              'AdminHTML.SubscriptionV3.PlanStatus.statusNotice_AppliedDiscount_Tooltip',
              {
                t1: (
                  <a href={URL['SubscriptionPlanDoc']}>
                    {i18n.translate('AdminHTML.Common.Action.learnMore')}
                  </a>
                )
              }
            )}
            icon={true}
          /> */}
        </p>
      );
    }

    return appliedDiscountMessage;
  };
}

export default withI18n()(PlanNoticeStatus);
